import { useCallback, useEffect, useMemo } from 'react';
import { useScope } from '@workspace/scope-context';

import { useTour, type TourDefinition, type TourMatchContext } from '@workspace/tour';
import { useSession } from '@/context/SessionContext';

const dashboardTours: TourDefinition[] = [
  {
    id: 'admin.accounts.overview',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/accounts' },
    steps: [
      {
        id: 'accounts-header',
        title: 'Accounts overview',
        description: 'Filter and inspect accounts; context chips show scope.',
        target: { anchorId: 'admin-accounts' },
        placement: 'bottom'
      },
      {
        id: 'accounts-help',
        title: 'Help & AI',
        description: 'Use Help or Ask AI for linking guidance (Ask AI is feature-flagged).',
        target: { anchorId: 'admin-accounts-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.account-detail',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/accounts/' },
    steps: [
      {
        id: 'account-detail-header',
        title: 'Account context',
        description: 'Header shows account ID and scope chips; actions on the right.',
        target: { anchorId: 'admin-account-detail' },
        placement: 'bottom'
      },
      {
        id: 'account-detail-help',
        title: 'Help & AI',
        description: 'Open Help or Ask AI for linking and status tips.',
        target: { anchorId: 'admin-account-detail-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.feature-flags',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/feature-flags' },
    steps: [
      {
        id: 'feature-flags-header',
        title: 'Feature flags',
        description: 'Search and toggle flags across environments.',
        target: { anchorId: 'admin-feature-flags' },
        placement: 'bottom'
      },
      {
        id: 'feature-flags-help',
        title: 'Help & AI',
        description: 'Use Help or Ask AI for rollout guidance.',
        target: { anchorId: 'admin-feature-flags-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.audit',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/audit' },
    steps: [
      {
        id: 'audit-header',
        title: 'Audit log',
        description: 'Filter by actor/date to trace activity.',
        target: { anchorId: 'admin-audit' },
        placement: 'bottom'
      },
      {
        id: 'audit-help',
        title: 'Help & AI',
        description: 'Use Help or Ask AI for investigation tips.',
        target: { anchorId: 'admin-audit-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.search-sync',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/search-sync' },
    steps: [
      {
        id: 'search-sync-header',
        title: 'Search sync status',
        description: 'Monitor index health; trigger sync or rebuild.',
        target: { anchorId: 'admin-search-sync' },
        placement: 'bottom'
      },
      {
        id: 'search-sync-help',
        title: 'Help & AI',
        description: 'Use Help or Ask AI for rebuild guidance.',
        target: { anchorId: 'admin-search-sync-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.users.overview',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/users' },
    steps: [
      {
        id: 'users-header',
        title: 'Users overview',
        description: 'Search and manage all users; context chips show the active tenant/global scope.',
        target: { anchorId: 'admin-users' },
        placement: 'bottom'
      },
      {
        id: 'users-help',
        title: 'Need help or AI suggestions?',
        description: 'Use Help or Ask AI to get guidance (Ask AI is feature-flagged).',
        target: { anchorId: 'admin-users-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.user-detail',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/users/' },
    steps: [
      {
        id: 'user-detail-header',
        title: 'User context',
        description: 'Header shows user identity with current tenant/global context chips.',
        target: { anchorId: 'admin-user-detail' },
        placement: 'bottom'
      },
      {
        id: 'user-access-tab',
        title: 'Manage access',
        description: 'Use the Platform/Organizations/Tenants/Accounts tabs to manage memberships and permissions in one workspace.',
        target: { anchorId: 'admin-user-detail-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.tenants.overview',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/tenants' },
    steps: [
      {
        id: 'tenants-header',
        title: 'Tenant directory',
        description: 'Review tenants and status; context chips remind you of current scope.',
        target: { anchorId: 'admin-tenants' },
        placement: 'bottom'
      },
      {
        id: 'tenants-help',
        title: 'Need help or AI suggestions?',
        description: 'Use Help or Ask AI for onboarding and status triage.',
        target: { anchorId: 'admin-tenants-help' },
        placement: 'left'
      }
    ]
  },
  {
    id: 'admin.tenant-detail',
    app: 'app-dashboard',
    version: 1,
    trigger: { path: '/admin/tenants/' },
    steps: [
      {
        id: 'tenant-detail-header',
        title: 'Tenant context',
        description: 'Header shows tenant ID and status; context chips highlight scope.',
        target: { anchorId: 'admin-tenant-detail' },
        placement: 'bottom'
      },
      {
        id: 'tenant-detail-help',
        title: 'Features and users',
        description: 'Use tabs to toggle features and manage users; confirm destructive actions.',
        target: { anchorId: 'admin-tenant-detail-help' },
        placement: 'left'
      }
    ]
  }
];

export function useDashboardTours() {
  const { registerTours } = useTour();

  useEffect(() => {
    const unregister = registerTours(dashboardTours);
    return unregister;
  }, [registerTours]);
}

function matchesTrigger(trigger: TourDefinition['trigger'], path: string) {
  if (!trigger?.path) {
    return false;
  }
  if (typeof trigger.path === 'string') {
    return trigger.path === path;
  }
  return trigger.path.test(path);
}

export function useDashboardManualTour(path: string) {
  const { session } = useSession();
  const scope = useScope();
  const { startTour, isReady } = useTour();

  const matchedTour = useMemo(() => {
    const context: TourMatchContext = { path, session, scope };
    for (const tour of dashboardTours) {
      if (!matchesTrigger(tour.trigger, path)) {
        continue;
      }
      if (tour.guard && !tour.guard(context)) {
        continue;
      }
      return tour;
    }
    return null;
  }, [path, scope, session]);

  const canStart = Boolean(isReady && matchedTour);
  const start = useCallback(() => {
    if (!matchedTour) {
      return false;
    }
    return startTour(matchedTour.id, { force: true });
  }, [matchedTour, startTour]);

  return { canStart, start, tourId: matchedTour?.id ?? null };
}
